import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search as SearchIcon, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { productService } from '../services/api';

// Product image component with fallback
const ProductImage = ({ src, alt, className, children }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Set a timeout to show fallback if image doesn't load within 3 seconds
  useEffect(() => {
    if (src && isLoading) {
      const timer = setTimeout(() => {
        if (isLoading) {
          setHasError(true);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [src, isLoading]);
  
  const getColorFromName = (name) => {
    const colors = [
      'from-pink-400 to-pink-600',
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-purple-400 to-purple-600',
      'from-orange-400 to-orange-600',
      'from-teal-400 to-teal-600',
      'from-red-400 to-red-600',
      'from-indigo-400 to-indigo-600',
    ];
    const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (hasError || !src) {
    return (
      <div className={`${className} bg-gradient-to-br ${getColorFromName(alt)} flex items-center justify-center relative`}>
        <div className="text-center text-white p-4">
          <Package className="h-12 w-12 mx-auto mb-2 opacity-80" />
          <p className="text-xs font-medium opacity-90 line-clamp-2">{alt}</p>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className={`${className} relative bg-gray-100`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
          <div className="animate-pulse text-gray-400">
            <Package className="h-8 w-8" />
          </div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onError={() => setHasError(true)}
        onLoad={() => setIsLoading(false)}
      />
      {children}
    </div>
  );
};

const Search = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const searchProducts = async () => {
      if (query) {
        setLoading(true);
        try {
          // Use backend search API for better performance
          const filtered = await productService.searchProducts(query);
          setResults(filtered);
        } catch (error) {
          console.error('Error searching products:', error);
          // Fallback to client-side search if API fails
          try {
            const allProducts = await productService.getAllProducts();
            const filtered = allProducts.filter(p =>
              p.name.toLowerCase().includes(query.toLowerCase()) ||
              p.description.toLowerCase().includes(query.toLowerCase()) ||
              p.product_code.toLowerCase().includes(query.toLowerCase()) ||
              p.category.toLowerCase().includes(query.toLowerCase())
            );
            setResults(filtered);
          } catch (e) {
            console.error('Fallback search also failed:', e);
          }
        } finally {
          setLoading(false);
        }
      }
    };

    searchProducts();
  }, [query]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Searching...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-pink-900 to-pink-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <SearchIcon className="h-8 w-8" />
            <h1 className="text-4xl font-bold">Search Results</h1>
          </div>
          <p className="text-pink-100">
            {results.length} result{results.length !== 1 ? 's' : ''} found for "{query}"
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {results.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {results.map((product) => (
              <Link key={product.product_code} to={`/product/${product.product_code}`}>
                <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                  <CardContent className="p-6">
                    <div className="relative mb-4">
                      <ProductImage
                        src={product.local_image ? `https://procloudpromo.com${product.local_image}` : product.image_url}
                        alt={product.name}
                        className="aspect-square rounded-lg overflow-hidden"
                      >
                        {product.is_new && (
                          <Badge className="absolute top-2 left-2 bg-pink-600">New</Badge>
                        )}
                      </ProductImage>
                    </div>
                    <h3 className="font-semibold text-lg mb-1 group-hover:text-pink-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">{product.product_code}</p>
                    <p className="text-sm text-gray-700 line-clamp-2">{product.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <SearchIcon className="h-24 w-24 mx-auto text-gray-400 mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              No results found for "{query}"
            </h2>
            <p className="text-gray-600 mb-8">
              Try adjusting your search or browse our categories
            </p>
            <Link to="/products/all">
              <Button size="lg">Browse All Products</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
